import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form';
import type { IconType } from 'react-icons';
import { FiEdit2 } from 'react-icons/fi';
import {
  TbFaceId,
  TbFingerprint,
  TbKey,
  TbMail,
  TbPhone,
  TbScanEye,
  TbShieldLock,
} from 'react-icons/tb';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { z } from 'zod';

import { API, postRequest } from '@/api/api';
import { getRefreshToken } from '@/api/tokens.helper';
import { type ApiException, isNotFound, isTooManyAttempts } from '@/common/error';
import { Header, Modal } from '@/components';
import { Button, ButtonOutlinedSmall } from '@/components/Button';
import { Input, PasswordInput, PhoneInput, Switch } from '@/components/Inputs';
import { isStrongPassword, WEAK_PASSWORD_MESSAGE } from '@/pages/Auth/passwordRules';
import PasswordStrength from '@/pages/Auth/PasswordStrength';
import CodeConfirm from '@/pages/Auth/TotpEnroll/CodeConfirm';
import CodeConfirmModal from '@/pages/Auth/TotpEnroll/CodeConfirmModal';
import TotpEnrollModal from '@/pages/Auth/TotpEnroll/TotpEnrollModal';
import {
  type AuthAccount,
  confirmLoginChange,
  getAuthAccount,
  requestLoginChange,
} from '@/store/auth/auth.api';
import { type TwoFactorEnabledResponse, useAuth } from '@/store/auth/auth.store';
import {
  type BiometryKind,
  canUseBiometric,
  disableBiometricLogin,
  enableBiometricLogin,
  getBiometryInfo,
  isBiometricEnabled,
} from '@/utils/biometric';
import { toast } from '@/utils/toast';

import { TWO_FACTOR_ROWS, type TwoFactorMethodKey } from './SecuritySettingsPage.data';
import s from './SecuritySettingsPage.module.scss';

const BIOMETRY_ICONS: Record<BiometryKind, IconType> = {
  face: TbFaceId,
  fingerprint: TbFingerprint,
  iris: TbScanEye,
  biometrics: TbShieldLock,
};

type LoginField = 'email' | 'phone';

const LOGIN_ROWS: { key: LoginField; icon: IconType; title: string }[] = [
  { key: 'email', icon: TbMail, title: 'Email' },
  { key: 'phone', icon: TbPhone, title: 'Mobile Number' },
];

const LOGIN_SUBJECT: Record<LoginField, string> = {
  email: 'email address',
  phone: 'mobile number',
};

/**
 * Turns a rejected login write — or the SMS enable that depends on it — into
 * something the user can act on. Auth wraps the Twilio or database reason, so it
 * only shows up in the text of the error body.
 */
const accountErrorText = (err: unknown, subject: string): string => {
  const e = err as { type?: string; dev_message?: string; error_message?: string } | null;
  const text = [e?.type, e?.dev_message, e?.error_message].filter(Boolean).join(' ');

  if (/DUPLICATE|already exists/i.test(text)) {
    return `Another account already uses this ${subject}. Please try a different one.`;
  }
  if (/matches the current/i.test(text)) {
    return `This is already your ${subject}. Please enter a different one to change it.`;
  }
  if (/TWILIO|short code|not a valid phone/i.test(text)) {
    return 'Couldn’t text this number. Please check it or try a different one.';
  }
  if (/INPUTS|Invalid email or phone/i.test(text)) {
    return `Couldn’t use that ${subject}. Please check it and try again.`;
  }
  return `Couldn’t save this ${subject}. Please try again.`;
};

/**
 * Turns a rejected login-change code into what to do next. Auth reads the token once,
 * so a wrong code burns it as surely as an expired one — both need a fresh code.
 */
const loginCodeErrorText = (err: unknown, subject: string): string => {
  const e = err as { dev_message?: string; error_message?: string } | null;
  const text = [e?.dev_message, e?.error_message].filter(Boolean).join(' ');

  if (/invalid code/i.test(text)) {
    return 'That code didn’t match, and it only works once. Please tap Resend Code for a new one.';
  }
  if (/token not found/i.test(text)) {
    return 'This code has expired. Please tap Resend Code for a new one.';
  }
  return accountErrorText(err, subject);
};

/**
 * Adds or replaces one half of the account login. Auth only makes the change once the
 * new email or number proves it is reachable: saving sends a code to it, and the change
 * lands when that code comes back.
 */
const LoginChangeModal = ({
  userId,
  field,
  initial = '',
  title,
  hint,
  onClose,
  onChanged,
}: {
  userId: string;
  field: LoginField;
  initial?: string;
  title: string;
  hint?: string;
  onClose: () => void;
  /** Fired once the change has landed, with the updated account. */
  onChanged: (next: AuthAccount) => void | Promise<void>;
}) => {
  const [value, setValue] = useState(initial);
  const [error, setError] = useState<string>();
  const [sending, setSending] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const changed = useRef<AuthAccount | null>(null);

  const login = value.trim();
  const valid =
    field === 'phone' ? isValidPhoneNumber(value) : z.string().email().safeParse(login).success;

  const send = async () => {
    if (!valid || sending) return;
    setSending(true);
    setError(undefined);
    try {
      setSecret(await requestLoginChange(userId, login));
    } catch (err) {
      console.error('Failed to request the login change:', err);
      setError(accountErrorText(err, LOGIN_SUBJECT[field]));
    } finally {
      setSending(false);
    }
  };

  if (secret) {
    return (
      <Modal open onClose={onClose}>
        <CodeConfirm
          method={field === 'phone' ? 'sms' : 'email'}
          destination={login}
          verify={async (code) => {
            changed.current = await confirmLoginChange(userId, secret, code);
          }}
          resend={async () => setSecret(await requestLoginChange(userId, login))}
          errorText={(err) => loginCodeErrorText(err, LOGIN_SUBJECT[field])}
          onDone={() => {
            if (changed.current) void onChanged(changed.current);
          }}
        />
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title={title}>
      <div className={s.modalBody}>
        {field === 'phone' ? (
          <PhoneInput
            label="Mobile Number"
            value={value}
            onChange={(v) => {
              setValue(v ?? '');
              setError(undefined);
            }}
            error={value && !valid ? 'Please enter a valid mobile number.' : error}
          />
        ) : (
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError(undefined);
            }}
            error={value && !valid ? 'Please enter a valid email address.' : error}
          />
        )}
        <p className={s.modalHint}>
          {hint ?? `You’ll get a code at this ${LOGIN_SUBJECT[field]} to confirm it’s yours.`}
        </p>
        <Button onClick={send} disabled={sending || !valid}>
          {sending ? 'Sending…' : 'Send Code'}
        </Button>
      </div>
    </Modal>
  );
};

/**
 * The email and mobile number the account signs in with. Both live on the auth user:
 * the profile's contact card edits people records, which are not logins, and each of
 * these is also where a code for its 2FA method is sent.
 */
const LoginGroup = ({
  account,
  onSaved,
}: {
  account: AuthAccount | null;
  onSaved: (next: AuthAccount) => void;
}) => {
  const [editing, setEditing] = useState<LoginField | null>(null);

  return (
    <>
      <p className={s.groupLabel}>Sign-In</p>
      <div className={s.group}>
        {LOGIN_ROWS.map(({ key, icon: Icon, title }) => (
          <div key={key} className={s.row}>
            <div className={s.rowIcon}>
              <Icon />
            </div>
            <div className={s.rowText}>
              <div className={s.rowTitle}>{title}</div>
              <div className={s.rowSub}>{account?.[key] || 'Not set'}</div>
            </div>
            <div className={s.rowActions}>
              <ButtonOutlinedSmall onClick={() => setEditing(key)} disabled={!account}>
                <FiEdit2 />
                {account?.[key] ? 'Edit' : 'Add'}
              </ButtonOutlinedSmall>
            </div>
          </div>
        ))}
      </div>
      <p className={s.heroHint}>
        You can sign in with either of these. Changing one also changes where your two-factor codes
        go.
      </p>

      {editing && account && (
        <LoginChangeModal
          userId={account.id}
          field={editing}
          initial={account[editing] ?? ''}
          title={`${account[editing] ? 'Change' : 'Add'} ${
            editing === 'phone' ? 'mobile number' : 'email'
          }`}
          onClose={() => setEditing(null)}
          onChanged={(next) => {
            onSaved(next);
            setEditing(null);
            toast.success(`Your ${LOGIN_SUBJECT[editing]} has been updated`);
          }}
        />
      )}
    </>
  );
};

const changePasswordSchema = z
  .object({
    current: z.string().min(1, 'Enter your current password'),
    next: z
      .string()
      .min(1, 'Enter a new password')
      .refine(isStrongPassword, { message: WEAK_PASSWORD_MESSAGE }),
    confirm: z.string().min(1, 'Enter the new password again'),
  })
  .refine((v) => !v.next || v.next !== v.current, {
    path: ['next'],
    message: 'The new password must be different from your current one',
  })
  .refine((v) => v.next === v.confirm, {
    path: ['confirm'],
    message: "The passwords don't match",
  });

type ChangePasswordValues = z.infer<typeof changePasswordSchema>;

/**
 * Says which field a rejected change belongs to and what to do about it. The current
 * password is checked by signing in with it, so a wrong one comes back as a failed
 * sign-in; the new one only reaches auth once that has passed.
 */
const changePasswordError = (
  err: unknown,
): { field: 'current' | 'next' | 'root'; message: string } => {
  const e = err as Partial<ApiException> | null;

  if (isTooManyAttempts(err)) {
    return {
      field: 'current',
      message: 'Too many wrong attempts. Wait a few minutes and try again.',
    };
  }
  if (isNotFound(err) || /AUTHENTICATION_METHOD/.test(e?.type ?? '')) {
    return {
      field: 'root',
      message: "Your account signs in without a password, so there isn't one to change.",
    };
  }
  if (e?.domain === 'BASIC_AUTH' && e.status === 401) {
    return { field: 'current', message: 'Your current password is incorrect' };
  }
  if (e?.type === 'INPUTS') {
    return { field: 'next', message: WEAK_PASSWORD_MESSAGE };
  }
  if (!e?.domain) {
    return {
      field: 'root',
      message: "Couldn't reach the server. Check your connection and try again.",
    };
  }
  return { field: 'root', message: "Couldn't change your password. Try again." };
};

/**
 * Changes the password the account signs in with. Auth signs every session out when it
 * does — other devices stay signed out, and this one is signed straight back in unless
 * the account has two-factor on, in which case the user signs in again here too.
 */
const ChangePasswordModal = ({ login, onClose }: { login: string; onClose: () => void }) => {
  const [signedOut, setSignedOut] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { current: '', next: '', confirm: '' },
  });
  const next = useWatch({ control, name: 'next' });

  const onSubmit: SubmitHandler<ChangePasswordValues> = async ({ current, next }) => {
    try {
      const stillSignedIn = await useAuth.getState().changePassword(login, current, next);
      if (!stillSignedIn) {
        setSignedOut(true);
        return;
      }
      toast.success('Your password has been changed');
      onClose();
    } catch (err) {
      console.error('Failed to change the password:', err);
      const { field, message } = changePasswordError(err);
      setError(field, { type: 'server', message });
    }
  };

  // The change has already signed this device out, so closing can only mean signing in.
  const signIn = () => void useAuth.getState().logout();

  if (signedOut) {
    return (
      <Modal open onClose={signIn} title="Password changed">
        <div className={s.modalBody}>
          <p className={s.modalHint}>
            Your password has been changed and you&apos;ve been signed out everywhere. Sign in again
            with your new password.
          </p>
          <Button onClick={signIn}>Sign In</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title="Change password">
      <form className={s.modalBody} onSubmit={handleSubmit(onSubmit)} noValidate>
        <PasswordInput
          label="Current password"
          autoComplete="current-password"
          {...register('current')}
          error={errors.current?.message}
        />
        <PasswordInput
          label="New password"
          autoComplete="new-password"
          {...register('next')}
          error={errors.next?.message}
        />
        <PasswordStrength password={next} />
        <PasswordInput
          label="Confirm new password"
          autoComplete="new-password"
          {...register('confirm')}
          error={errors.confirm?.message}
        />
        {errors.root && (
          <p role="alert" className={s.formError}>
            {errors.root.message}
          </p>
        )}
        <p className={s.modalHint}>You&apos;ll be signed out on your other devices.</p>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Changing…' : 'Change password'}
        </Button>
      </form>
    </Modal>
  );
};

const PasswordGroup = ({ account }: { account: AuthAccount | null }) => {
  const [open, setOpen] = useState(false);
  const login = account?.email ?? account?.phone;

  return (
    <>
      <p className={s.groupLabel}>Password</p>
      <div className={s.group}>
        <div className={s.row}>
          <div className={s.rowIcon}>
            <TbKey />
          </div>
          <div className={s.rowText}>
            <div className={s.rowTitle}>Password</div>
            <div className={s.rowSub}>Change the password you sign in with</div>
          </div>
          <div className={s.rowActions}>
            <ButtonOutlinedSmall onClick={() => setOpen(true)} disabled={!login}>
              <FiEdit2 />
              Change
            </ButtonOutlinedSmall>
          </div>
        </div>
      </div>

      {open && login && <ChangePasswordModal login={login} onClose={() => setOpen(false)} />}
    </>
  );
};

const TwoFactorGroup = ({
  account,
  onAccount,
}: {
  account: AuthAccount | null;
  onAccount: (next: AuthAccount) => void;
}) => {
  const session = useAuth((st) => st.session);
  const profilePhone = session?.contacts?.find((c) => c.contact_type === 'phone')?.contact;
  const smsUserId = account?.id ?? session?.id;

  const [status, setStatus] = useState<TwoFactorEnabledResponse | null>(null);
  const [busy, setBusy] = useState<TwoFactorMethodKey | null>(null);
  const [qrCode, setQrCode] = useState('');
  const [totpUrl, setTotpUrl] = useState('');
  const [codeMethod, setCodeMethod] = useState<'email' | 'sms' | null>(null);
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);

  // Load the current status on mount. Store actions and the setter are stable, so the
  // effect needs no extra dependencies.
  useEffect(() => {
    useAuth
      .getState()
      .getTwoFactorEnabled()
      .then(setStatus)
      .catch((err) => console.error('Failed to load 2FA status:', err));
  }, []);

  const refresh = async () => {
    try {
      setStatus(await useAuth.getState().getTwoFactorEnabled());
    } catch (err) {
      console.error('Failed to refresh 2FA status:', err);
    }
  };

  const enabledCount = status
    ? (['email', 'totp', 'sms'] as TwoFactorMethodKey[]).filter((m) => status[m]).length
    : 0;
  // The default only matters once the user has a choice to make.
  const showDefaultPicker = enabledCount >= 2;

  const toggle = async (method: TwoFactorMethodKey) => {
    if (busy) return;
    const auth = useAuth.getState();
    setBusy(method);
    try {
      if (status?.[method]) {
        await auth.disableTwoFactorMethod(method);
        await refresh();
        toast.success('Two-factor method turned off');
        return;
      }

      if (method === 'totp') {
        const { qr_code, url } = await auth.enableTotp();
        setQrCode(qr_code);
        setTotpUrl(url);
        await refresh();
        return;
      }

      /* Both code methods send to the login on the auth account, so neither can be
         armed without that half of it. Re-read the account when it hasn't loaded:
         treating a failed load as "no number" would overwrite a login that is there. */
      const current = account ?? (await getAuthAccount());
      onAccount(current);

      if (method === 'sms') {
        // With a phone login already on the account — the number the Login block
        // shows — arm SMS straight away; only ask when the account has none.
        if (!current.phone) {
          setPhoneModalOpen(true);
          return;
        }

        await auth.enableTwoFactorMethod('sms');
        setCodeMethod('sms');
        return;
      }

      if (!current.email) {
        toast.error('Please add an email address under Sign-in first.');
        return;
      }

      // Same for email: arm + send code, then confirm in the modal.
      await auth.enableTwoFactorMethod('email');
      setCodeMethod('email');
    } catch (err) {
      console.error('Failed to update 2FA method:', err);
      toast.error('Couldn’t update your two-factor settings. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  /* The code is texted to the phone on the *auth* account, and auth fills that column
     from the account login alone — a phone contact written to the people service never
     reaches it, so enabling SMS failed with "No phone number found". The prompt only
     opens for an account with no phone login, so it adds one — confirmed by a code of
     its own first; auth leaves the email half of the login untouched. */
  const enableSmsWithPhone = async (next: AuthAccount) => {
    setPhoneModalOpen(false);
    onAccount(next);
    try {
      // Keep the profile in step when it has no number of its own yet; an existing one
      // is the user's and isn't ours to overwrite from the security screen.
      if (!profilePhone && next.phone) {
        await postRequest(API.PEOPLE, `/${next.id}/contacts`, {
          contacts: [{ contact_type: 'phone', contact: next.phone }],
        });
        await useAuth.getState().me();
      }

      await useAuth.getState().enableTwoFactorMethod('sms'); // arms + sends the code
      setCodeMethod('sms'); // confirm the code before SMS counts as enabled
    } catch (err) {
      console.error('Failed to enable SMS 2FA:', err);
      toast.error('Couldn’t turn on SMS codes. Please try again.');
    }
  };

  const makeDefault = async (method: TwoFactorMethodKey) => {
    try {
      await useAuth.getState().setDefaultTwoFactor(method);
      await refresh();
    } catch (err) {
      console.error('Failed to set default 2FA method:', err);
      toast.error('Couldn’t set the default method. Please try again.');
    }
  };

  return (
    <>
      <p className={s.groupLabel}>Two-Factor Authentication</p>
      <div className={s.group}>
        {TWO_FACTOR_ROWS.map(({ key, icon: Icon, title, sub }) => {
          const on = !!status?.[key];
          const isDefault = status?.default === key;
          return (
            <div key={key} className={clsx(s.row, busy === key && s.rowBusy)}>
              <div className={s.rowIcon}>
                <Icon />
              </div>
              <div className={s.rowText}>
                <div className={s.rowTitle}>{title}</div>
                <div className={s.rowSub}>
                  {on && showDefaultPicker ? (
                    isDefault ? (
                      'Default method'
                    ) : (
                      <button type="button" className={s.linkBtn} onClick={() => makeDefault(key)}>
                        Set as Default
                      </button>
                    )
                  ) : (
                    sub
                  )}
                </div>
              </div>
              <Switch checked={on} onChange={() => toggle(key)} disabled={busy !== null} />
            </div>
          );
        })}
      </div>
      <p className={s.heroHint}>
        Require a second step when signing in. We&apos;ll prompt for your default method first.
      </p>

      {qrCode && (
        <TotpEnrollModal
          qrCode={qrCode}
          url={totpUrl}
          onClose={() => {
            setQrCode('');
            setTotpUrl('');
          }}
          onConfirmed={async () => {
            await refresh();
            toast.success('Authenticator app enabled');
          }}
        />
      )}

      {codeMethod && (
        <CodeConfirmModal
          method={codeMethod}
          onClose={() => setCodeMethod(null)}
          onConfirmed={async () => {
            await refresh();
            toast.success(
              codeMethod === 'sms' ? 'SMS two-factor enabled' : 'Email two-factor enabled',
            );
          }}
        />
      )}

      {phoneModalOpen && smsUserId && (
        <LoginChangeModal
          userId={smsUserId}
          field="phone"
          initial={profilePhone ?? ''}
          title="Mobile Number for SMS"
          hint="Sign-in codes go to this number, and you can sign in with it. A code comes first to confirm it’s yours."
          onClose={() => setPhoneModalOpen(false)}
          onChanged={enableSmsWithPhone}
        />
      )}
    </>
  );
};

const SecurityContent = () => {
  // Capability is probed once on mount and held in state, so the toggle's click
  // handler can call enableBiometricLogin() with minimal async before WebAuthn —
  // Safari requires the prompt to fire within the click's transient activation.
  const [ready, setReady] = useState(false);
  const [available, setAvailable] = useState(false);
  const [enabled, setEnabled] = useState(isBiometricEnabled());
  const [label, setLabel] = useState('Biometrics');
  const [kind, setKind] = useState<BiometryKind>('biometrics');
  const [busy, setBusy] = useState(false);
  // The auth account is the login side of the user — its email and phone are both
  // what signs in and where code-based 2FA is sent, so both blocks below read it.
  const [account, setAccount] = useState<AuthAccount | null>(null);

  const email = useAuth(
    (st) => st.session?.contacts?.find((c) => c.contact_type === 'email')?.contact,
  );

  useEffect(() => {
    let active = true;
    (async () => {
      const [can, info] = await Promise.all([canUseBiometric(), getBiometryInfo()]);
      if (!active) return;
      setAvailable(can);
      setLabel(info.label);
      setKind(info.kind);
      setReady(true);
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    getAuthAccount()
      .then(setAccount)
      .catch((err) => console.error('Failed to load the auth account:', err));
  }, []);

  const handleToggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (enabled) {
        await disableBiometricLogin();
        setEnabled(false);
        toast.success(`Your ${label} sign-in has been turned off.`);
        return;
      }

      const token = getRefreshToken();
      if (!token) {
        toast.error(`Please sign in again to turn on ${label} sign-in.`);
        return;
      }
      // Web: this triggers the WebAuthn credential creation + biometric prompt.
      await enableBiometricLogin(token, email);
      setEnabled(true);
      toast.success(`Your ${label} sign-in has been turned on.`);
    } catch (err) {
      // Common web failure: the browser/authenticator lacks the PRF extension, or
      // the user dismissed the system prompt. Keep the toggle off and be honest.
      console.error('Failed to update biometric login:', err);
      toast.error(
        enabled
          ? 'Couldn’t turn off biometric sign-in. Please try again.'
          : `Couldn’t turn on ${label} sign-in. Please try again.`,
      );
    } finally {
      setBusy(false);
    }
  };

  const Icon = BIOMETRY_ICONS[kind];

  const subtitle = !ready
    ? 'Checking availability…'
    : !available
      ? 'Not available on this device or browser'
      : enabled
        ? 'Skip your password and unlock with your face or fingerprint.'
        : 'Use biometrics to sign in faster next time.';

  return (
    <div className={s.page}>
      <header className={s.detailHeader}>
        <div className={s.detailIcon}>
          <TbShieldLock />
        </div>
        <div>
          <h2 className={s.detailTitle}>Security</h2>
          <p className={s.detailSub}>Manage how you sign in to VesselHaven.</p>
        </div>
      </header>

      <p className={s.groupLabel}>Sign-in</p>
      <div className={s.group}>
        <div className={clsx(s.row, busy && s.rowBusy)}>
          <div className={s.rowIcon}>
            <Icon />
          </div>
          <div className={s.rowText}>
            <div className={s.rowTitle}>{label} login</div>
            <div className={s.rowSub}>{subtitle}</div>
          </div>
          {ready && available && <Switch checked={enabled} onChange={handleToggle} />}
        </div>
      </div>
      <p className={s.heroHint}>
        Your biometrics never leave this device. We only keep an encrypted sign-in token that your
        face or fingerprint unlocks.
      </p>

      <PasswordGroup account={account} />

      <LoginGroup account={account} onSaved={setAccount} />

      <TwoFactorGroup account={account} onAccount={setAccount} />
    </div>
  );
};

export default function SecuritySettingsPage({ embedded = false }: { embedded?: boolean } = {}) {
  return (
    <>
      {!embedded && <Header title="Security" backButton />}
      <SecurityContent />
    </>
  );
}
